import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, Package, Search, ShoppingBag, User } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { NAV } from "../../data/catalog";
import { Logo } from "./Logo";
import { MobileMenu } from "./MobileMenu";
import { SearchOverlay } from "./SearchOverlay";

const Icon = ({ children, label, testid, className = "", ...rest }) => (
  <button aria-label={label} data-testid={testid} className={`relative grid h-10 w-10 place-items-center rounded-full transition-opacity duration-300 hover:opacity-70 ${className}`} {...rest}>
    {children}
  </button>
);

export const Header = () => {
  const { pathname } = useLocation();
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const clear = (pathname === "/" || pathname === "/our-story") && !scrolled;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,color] duration-500 ${
          clear ? "border-transparent bg-transparent text-white" : "border-line bg-ivory/85 text-ink backdrop-blur-xl"
        }`}
      >
        <div className={`overflow-hidden bg-ink text-ivory transition-[max-height] duration-500 ${scrolled ? "max-h-0" : "max-h-10"}`}>
          <p data-testid="announcement-bar" className="py-2 text-center font-mono text-[10px] uppercase tracking-[0.28em] sm:text-[11px]">
            Collection 01 is here<span className="hidden sm:inline"> · Six signature colors · Secure checkout</span>
          </p>
        </div>
        <div className="mx-auto flex h-[76px] max-w-[1600px] items-center gap-4 px-4 md:px-10">
          <Icon label="Open menu" testid="nav-menu-button" className="min-[1440px]:hidden" onClick={() => setMenu(true)}>
            <Menu className="h-5 w-5" />
          </Icon>
          <Logo className="-ml-2 min-[1440px]:ml-0" />
          <nav className="mx-auto hidden items-center gap-5 whitespace-nowrap min-[1440px]:flex 2xl:gap-7" aria-label="Main">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `link-line text-[12px] font-medium uppercase tracking-[0.2em] ${isActive ? "is-active" : ""}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center min-[1440px]:ml-0">
            <Icon label="Search" testid="nav-search-button" onClick={() => setSearch(true)}><Search className="h-[19px] w-[19px]" /></Icon>
            <Link to="/track-order" aria-label="Track order" data-testid="nav-track-order-link" className="hidden h-10 w-10 place-items-center rounded-full sm:grid"><Package className="h-[19px] w-[19px]" /></Link>
            <Link to="/account" aria-label="Account" data-testid="nav-account-link" className="hidden h-10 w-10 place-items-center rounded-full sm:grid"><User className="h-[19px] w-[19px]" /></Link>
            <Icon label="Open cart" testid="nav-cart-button" onClick={() => setOpen(true)}>
              <ShoppingBag className="h-[19px] w-[19px]" />
              {count > 0 && (
                <span data-testid="nav-cart-count" className="absolute right-0.5 top-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[#C9CDD2] px-1 text-[10px] font-semibold text-ink">{count}</span>
              )}
            </Icon>
          </div>
        </div>
      </header>
      <MobileMenu open={menu} onOpenChange={setMenu} />
      <SearchOverlay open={search} onOpenChange={setSearch} />
    </>
  );
};
